/**
 * Direct chain ops for agent memory — commit a new encrypted version
 * and roll back to a prior one.
 *
 * Unlike the rest of the SDK (which posts signed hex to the judge
 * endpoint and lets the server submit), these ops sign locally and
 * submit straight to Postchain via `postchain-client`. Op names, arg
 * ordering, and response parsing all live in the Rust core so every
 * binding stays in sync; transport (this file) is what varies per
 * language.
 *
 * Plaintext never leaves the SDK process — encryption / decryption
 * happens in the Rust core via `./crypto.ts` using a key derived from
 * the agent's privkey.
 */
import postchain, { type RawGtv } from "postchain-client";

import { PUBLIC_CHAIN, chainForNetwork, type ChainConfig } from "../chain-config.js";
import { Atbash } from "../client.js";
import { native } from "../native.js";
import type { AgentAuth, ChainOpts } from "../types.js";
import {
  decryptMemoryContent,
  deriveMemoryKey,
  encryptMemoryContent,
} from "./crypto.js";

// postchain-client re-exports the exact `buffer/` polyfill Buffer it
// uses internally for `Buffer.isBuffer()` checks. Using a separately
// imported polyfill instance would produce Buffer objects whose class
// identity doesn't match postchain's internal check — fine in ESM
// (the bundler deduplicates), fails in CJS. Routing through the
// re-export guarantees class identity in both build targets.
const { createClient, encryption, newSignatureProvider, Buffer: PolyBuffer } =
  postchain as typeof postchain & {
    Buffer: { from: (data: Uint8Array | Buffer) => Buffer };
  };

function toGtxBytes(bytes: Buffer | Uint8Array): Buffer {
  return PolyBuffer.from(new Uint8Array(bytes));
}

interface ChainResolveOpts {
  orgName?: string;
  endpoint?: string;
  chainOpts?: ChainOpts;
}

/**
 * Resolve `orgName` → chain when needed. Callers pass `orgName` and
 * we ask the dashboard which chain that org lives on; if `chainOpts`
 * already pins a `blockchainRid`, we respect it.
 */
async function resolveChainOptsForOrg(
  opts?: ChainResolveOpts,
  auth?: AgentAuth,
): Promise<ChainOpts | undefined> {
  if (opts?.orgName && !opts.chainOpts?.blockchainRid && auth) {
    const atbash = new Atbash(auth.privkey, {
      endpoint: opts.endpoint,
      orgName: opts.orgName,
    });
    const resolved = await atbash.resolveChainForOrg(opts.orgName);
    return { ...opts.chainOpts, network: resolved.network };
  }
  return opts?.chainOpts;
}

/** Turn `chainOpts` into concrete `{ nodeUrls, blockchainRid }`. */
function materializeChain(chainOpts?: ChainOpts): {
  nodeUrls: readonly string[];
  blockchainRid: string;
} {
  if (chainOpts?.blockchainRid && chainOpts.nodeUrls) {
    return {
      nodeUrls: chainOpts.nodeUrls,
      blockchainRid: chainOpts.blockchainRid,
    };
  }
  const config: ChainConfig = chainOpts?.network
    ? chainForNetwork(chainOpts.network)
    : PUBLIC_CHAIN;
  return {
    nodeUrls: chainOpts?.nodeUrls ?? config.nodeUrls,
    blockchainRid: chainOpts?.blockchainRid ?? config.blockchainRid,
  };
}

async function buildChainClient(chainOpts?: ChainOpts) {
  const { nodeUrls, blockchainRid } = materializeChain(chainOpts);
  return createClient({ nodeUrlPool: [...nodeUrls], blockchainRid });
}

function buildSigner(auth: AgentAuth) {
  const privKeyBuf = Buffer.from(auth.privkey, "hex");
  const keyPair = encryption.makeKeyPair(privKeyBuf);
  const sigProvider = newSignatureProvider({
    privKey: keyPair.privKey,
    pubKey: keyPair.pubKey,
  });
  return { keyPair, sigProvider };
}

export interface CommitMemoryOptions {
  /** Score in [1, 10]. Defaults to 5 (matches the Rell entity default). */
  score?: number;
  /** Org name — when set, the SDK resolves which chain the agent lives on. */
  orgName?: string;
  /** Atbash service endpoint for org→chain lookup. */
  endpoint?: string;
  chainOpts?: ChainOpts;
}

export interface RollbackMemoryOptions {
  orgName?: string;
  endpoint?: string;
  chainOpts?: ChainOpts;
}

/**
 * Encrypt `plaintext` and commit it as a new memory version via
 * `add_agent_memory`. The previous active version (if any) is
 * deactivated on-chain.
 *
 * The caller is responsible for running `scanMemory` first when
 * appropriate — this function does not gate on the verdict.
 */
export async function commitMemoryVersion(
  plaintext: string,
  auth: AgentAuth,
  opts?: CommitMemoryOptions,
): Promise<void> {
  const score = opts?.score ?? 5;

  const key = await deriveMemoryKey(auth.privkey);
  const { ciphertext, nonce } = await encryptMemoryContent(plaintext, key);

  const args = native.buildAddAgentMemoryArgs(
    auth.pubkey,
    ciphertext,
    nonce,
    score,
  );

  const chainOpts = await resolveChainOptsForOrg(opts, auth);
  const client = await buildChainClient(chainOpts);
  const { sigProvider } = buildSigner(auth);

  await client.signAndSendUniqueTransaction(
    {
      name: native.OP_ADD_AGENT_MEMORY,
      args: [
        toGtxBytes(args.agent_pubkey),
        toGtxBytes(args.content_cipher),
        toGtxBytes(args.nonce),
        args.score,
      ],
    },
    sigProvider,
  );
}

/**
 * Decrypted memory entry returned by the read helpers.
 *
 * `content` is the plaintext recovered from on-chain ciphertext. If
 * decryption fails (corrupted row, wrong key, etc.), `content` is
 * empty and `decryptError` carries the reason — the SDK returns the
 * row instead of throwing so one bad entry doesn't hide the rest.
 */
export interface AgentMemoryEntry {
  id: number;
  content: string;
  decryptError?: string;
  score: number;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

/** Rollback event from `memory_rollback_log`. */
export interface MemoryRollbackEvent {
  fromId: number;
  toId: number;
  reason: string;
  signer: string;
  createdAt: number;
}

async function decryptRow(
  row: unknown,
  key: Buffer,
): Promise<AgentMemoryEntry> {
  const parsed = native.parseMemoryRow(row as RawGtv);
  let content: string;
  let decryptError: string | undefined;
  try {
    content = await decryptMemoryContent(
      Buffer.from(parsed.content_cipher),
      Buffer.from(parsed.nonce),
      key,
    );
  } catch (err) {
    content = "";
    decryptError = err instanceof Error ? err.message : String(err);
  }
  return {
    id: parsed.id,
    content,
    decryptError,
    score: parsed.score,
    isActive: parsed.is_active,
    createdAt: parsed.created_at,
    updatedAt: parsed.updated_at,
  };
}

/**
 * Cheap version-pointer probe. Returns just the id of the current
 * active memory (or null if none). No ciphertext is transferred — the
 * response is a single integer, so this is safe to call on every
 * memory-read hot path.
 */
export async function getActiveMemoryId(
  auth: AgentAuth,
  chainOpts?: ChainOpts,
): Promise<number | null> {
  const client = await buildChainClient(chainOpts);
  const q = native.buildGetActiveMemoryIdQuery(auth.pubkey);
  const raw = await client.query(native.QUERY_GET_ACTIVE_MEMORY_ID, {
    [native.ARG_AGENT_PUBKEY]: q.agent_pubkey_hex,
  });
  return native.parseActiveMemoryId(raw);
}

/**
 * Recent active memory entries — subset of active versions filtered
 * by the chain's `MEMORY_RECENT_WINDOW_MS`. For a time-unbounded view
 * of every currently active version, use `getAllAgentMemory`.
 */
export async function getActiveMemory(
  auth: AgentAuth,
  chainOpts?: ChainOpts,
): Promise<AgentMemoryEntry[]> {
  const client = await buildChainClient(chainOpts);
  const key = await deriveMemoryKey(auth.privkey);
  const q = native.buildGetAgentMemoryQuery(auth.pubkey);
  const rows = (await client.query(native.QUERY_GET_AGENT_MEMORY, {
    [native.ARG_AGENT_PUBKEY]: q.agent_pubkey_hex,
  })) as unknown[];
  return Promise.all((rows ?? []).map((r) => decryptRow(r, key)));
}

/**
 * All currently-active memory entries with no time cutoff. Use this
 * when you need every active version regardless of age.
 */
export async function getAllAgentMemory(
  auth: AgentAuth,
  chainOpts?: ChainOpts,
): Promise<AgentMemoryEntry[]> {
  const client = await buildChainClient(chainOpts);
  const key = await deriveMemoryKey(auth.privkey);
  const q = native.buildGetAllAgentMemoryQuery(auth.pubkey);
  const rows = (await client.query(native.QUERY_GET_ALL_AGENT_MEMORY, {
    [native.ARG_AGENT_PUBKEY]: q.agent_pubkey_hex,
  })) as unknown[];
  return Promise.all((rows ?? []).map((r) => decryptRow(r, key)));
}

/**
 * Full version history — active + inactive, most recent first. Used
 * by rollback UX to choose a target version.
 */
export async function getMemoryHistory(
  auth: AgentAuth,
  chainOpts?: ChainOpts,
): Promise<AgentMemoryEntry[]> {
  const client = await buildChainClient(chainOpts);
  const key = await deriveMemoryKey(auth.privkey);
  const q = native.buildGetAgentMemoryHistoryQuery(auth.pubkey);
  const rows = (await client.query(native.QUERY_GET_AGENT_MEMORY_HISTORY, {
    [native.ARG_AGENT_PUBKEY]: q.agent_pubkey_hex,
  })) as unknown[];
  return Promise.all((rows ?? []).map((r) => decryptRow(r, key)));
}

/**
 * Fetch a single memory entry by version id, including its current
 * `is_active` state.
 */
export async function getMemoryById(
  id: number,
  auth: AgentAuth,
  chainOpts?: ChainOpts,
): Promise<AgentMemoryEntry> {
  const client = await buildChainClient(chainOpts);
  const key = await deriveMemoryKey(auth.privkey);
  const q = native.buildGetAgentMemoryByIdQuery(auth.pubkey, id);
  const row = await client.query(native.QUERY_GET_AGENT_MEMORY_BY_ID, {
    [native.ARG_AGENT_PUBKEY]: q.agent_pubkey_hex,
    [native.ARG_ID]: q.id,
  });
  return decryptRow(row, key);
}

/**
 * Audit trail of rollback events for this agent, most recent first.
 */
export async function getRollbackHistory(
  auth: AgentAuth,
  chainOpts?: ChainOpts,
): Promise<MemoryRollbackEvent[]> {
  const client = await buildChainClient(chainOpts);
  const q = native.buildGetAgentMemoryRollbackHistoryQuery(auth.pubkey);
  const raw = (await client.query(
    native.QUERY_GET_AGENT_MEMORY_ROLLBACK_HISTORY,
    { [native.ARG_AGENT_PUBKEY]: q.agent_pubkey_hex },
  )) as unknown[];
  const parsed = native.parseRollbackRows(raw ?? []);
  return parsed.map((r) => ({
    fromId: r.from_id,
    toId: r.to_id,
    reason: r.reason,
    signer: Buffer.from(r.signer).toString("hex"),
    createdAt: r.created_at,
  }));
}

/**
 * Roll back to a previously-committed memory version. The target
 * `toId` must exist and be currently inactive. `reason` is required
 * and is recorded on-chain in `memory_rollback_log`.
 */
export async function rollbackMemory(
  toId: number,
  reason: string,
  auth: AgentAuth,
  opts?: RollbackMemoryOptions,
): Promise<void> {
  const args = native.buildRollbackAgentMemoryArgs(auth.pubkey, toId, reason);

  const chainOpts = await resolveChainOptsForOrg(opts, auth);
  const client = await buildChainClient(chainOpts);
  const { sigProvider } = buildSigner(auth);

  await client.signAndSendUniqueTransaction(
    {
      name: native.OP_ROLLBACK_AGENT_MEMORY,
      args: [toGtxBytes(args.agent_pubkey), args.to_id, args.reason],
    },
    sigProvider,
  );
}
